import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Link2Off, Hammer, Shield, Coins, TrendingUp, Unlock, Crown } from 'lucide-react';
import { Button } from './ui/button';

// Stage Icons mapping
const STAGE_ICONS = {
  1: Link2Off,      // Broken chain for Dependence
  2: Hammer,        // Hammer for Productivity
  3: Shield,        // Shield for Stability
  4: Coins,         // Coins for Independence
  5: TrendingUp,    // Trending chart for Growth
  6: Unlock,        // Open lock for Freedom
  7: Crown,         // Crown for Abundance/Legacy
};

const STAGES_DATA = [
  { key: 1, name: "Supported", label: "Dependence", color: "#DC2626" },
  { key: 2, name: "Builder", label: "Productivity", color: "#EA580C" },
  { key: 3, name: "Stabilizer", label: "Stability", color: "#F59E0B" },
  { key: 4, name: "Accumulator", label: "Independence", color: "#84CC16" },
  { key: 5, name: "Investor", label: "Growth", color: "#059669" },
  { key: 6, name: "Life Owner", label: "Freedom", color: "#1E3A8A" },
  { key: 7, name: "Legacy Designer", label: "Abundance", color: "#8B5CF6" },
];

const STAGE_DESCRIPTIONS = {
  1: "No income, you depend on others to cover expenses. Limited ability to make decisions",
  2: "By exploiting your skills, you have sources of income, but don't cover your expenses",
  3: "Your income covers your expenses, sometimes you can save money",
  4: "Savings are now generated in a stable way, and you also generate an extra income from savings",
  5: "Income from investments (rents or interests) cover your basic expenses. You don't have expensive debt, and can diversify investments.",
  6: "Income from your investments cover all of your expenses with your lifestyle and a bit more. You don't have to work for money anymore",
  7: "Income from your investment and business cover far beyond your lifestyle. Money is not a problem anymore, you start thinking about legacy."
};

const EXPANDED_DESCRIPTIONS = {
  1: {
    advancing: {
      title: "You are currently in Dependence, but you are taking steps toward self-sufficiency.",
      body: "You still rely on others or debt to cover expenses, but your trajectory shows early movement toward earning and control.",
      priority: "Priority: build consistent income and eliminate structural vulnerability."
    },
    steady: {
      title: "You are in Dependence, and nothing is structurally improving.",
      body: "You remain financially exposed and dependent on external support.",
      priority: "Without immediate action, this stage tends to deepen."
    },
    contracting: {
      title: "You are in Dependence, and your financial stability is weakening.",
      body: "Support systems or temporary solutions are eroding.",
      priority: "Urgent focus on income generation is required."
    }
  },
  2: {
    advancing: {
      title: "You generate income through your skills, but not enough to fully sustain your lifestyle yet.",
      body: "Your earning capacity is improving.",
      priority: "Focus now: create margin between income and expenses."
    },
    steady: {
      title: "You are working and producing, but not progressing financially.",
      body: "Effort exists without structural improvement.",
      priority: "Without margin, growth stalls."
    },
    contracting: {
      title: "Income pressure is increasing.",
      body: "Expenses may be rising faster than earnings.",
      priority: "Without correction, regression is possible."
    }
  },
  3: {
    advancing: {
      title: "Your income covers expenses, and you may have small surpluses.",
      body: "You are building breathing room.",
      priority: "Next step: convert surplus into assets."
    },
    steady: {
      title: "You are stable, but not accumulating.",
      body: "Covered expenses without asset growth leads to plateau.",
      priority: ""
    },
    contracting: {
      title: "Your margin is shrinking.",
      body: "Lifestyle inflation or rising costs are eroding stability.",
      priority: ""
    }
  },
  4: {
    advancing: {
      title: "Savings are consistent, and assets are beginning to generate income.",
      body: "You are transitioning from worker to owner.",
      priority: "Focus: disciplined compounding."
    },
    steady: {
      title: "You are saving, but momentum has slowed.",
      body: "Assets are not expanding meaningfully.",
      priority: ""
    },
    contracting: {
      title: "Withdrawals or inconsistency are reducing progress.",
      body: "Without reinvestment, independence reverses.",
      priority: ""
    }
  },
  5: {
    advancing: {
      title: "Investment income covers basic expenses.",
      body: "Work is optional for survival.",
      priority: "Focus: diversification and resilience."
    },
    steady: {
      title: "Assets sustain you, but growth has slowed.",
      body: "Reallocation may be required.",
      priority: ""
    },
    contracting: {
      title: "Asset erosion or overexposure is visible.",
      body: "Correction needed to preserve independence.",
      priority: ""
    }
  },
  6: {
    advancing: {
      title: "Investment income covers your lifestyle and continues to grow.",
      body: "Work is a choice.",
      priority: "Focus: long-term positioning and structural resilience."
    },
    steady: {
      title: "Freedom is sustained, but growth has plateaued.",
      body: "Optimization and protection become critical.",
      priority: ""
    },
    contracting: {
      title: "Spending, risk concentration, or shocks are reducing buffer.",
      body: "Without protection, freedom narrows.",
      priority: ""
    }
  },
  7: {
    advancing: {
      title: "Income exceeds lifestyle needs significantly.",
      body: "Focus shifts to impact and legacy.",
      priority: ""
    },
    steady: {
      title: "Capital growth is stable but not accelerating.",
      body: "Strategic deployment becomes the next evolution.",
      priority: ""
    },
    contracting: {
      title: "Concentration risk or inefficiencies weaken long-term position.",
      body: "Preservation and governance are priorities.",
      priority: ""
    }
  }
};

const QuizResultCarousel = ({ quizResult, onRetake }) => {
  const currentLevel = quizResult.stage_level;
  const direction = quizResult.direction;
  
  const [carouselIndex, setCarouselIndex] = useState(currentLevel - 1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const viewedStage = STAGES_DATA[carouselIndex];
  const isCurrentStage = carouselIndex === currentLevel - 1;
  
  const directionText = direction === 'advancing' ? 'Advancing' : 
                       direction === 'contracting' ? 'Declining' : 
                       (currentLevel <= 3 ? 'Stagnant' : 'Steady');
  
  const directionColor = direction === 'advancing' ? '#22C55E' : 
                        direction === 'contracting' ? '#EF4444' : '#9CA3AF';
  
  const currentDescription = EXPANDED_DESCRIPTIONS[currentLevel]?.[direction] || EXPANDED_DESCRIPTIONS[currentLevel]?.steady;
  
  const handlePrev = () => {
    if (carouselIndex > 0) setCarouselIndex(carouselIndex - 1);
  };
  
  const handleNext = () => {
    if (carouselIndex < 6) setCarouselIndex(carouselIndex + 1);
  };
  
  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrev();
  };

  return (
    <div className="space-y-6">
      {/* Direction Indicator - Ladder Style (only shown when viewing current stage) */}
      {isCurrentStage && (
        <div className="flex justify-center">
          <svg width="280" height="70" viewBox="0 0 280 70">
            <defs>
              <linearGradient id="quizBlueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1E40AF" />
                <stop offset="50%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#1E40AF" />
              </linearGradient>
              <linearGradient id="quizRedGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#991B1B" />
                <stop offset="50%" stopColor="#DC2626" />
                <stop offset="100%" stopColor="#991B1B" />
              </linearGradient>
              <linearGradient id="quizGrayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="50%" stopColor="#9CA3AF" />
                <stop offset="100%" stopColor="#4B5563" />
              </linearGradient>
              <filter id="quizShadow">
                <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="rgba(0,0,0,0.4)" />
              </filter>
            </defs>
            
            {direction === 'advancing' && (
              <g transform="translate(0, 10)">
                <rect x="0" y="10" width="220" height="40" rx="20" fill="url(#quizBlueGradient)" filter="url(#quizShadow)" />
                <polygon points="200,0 260,30 200,60" fill="url(#quizBlueGradient)" filter="url(#quizShadow)" />
                <text x="100" y="38" fontSize="18" fontWeight="700" fill="white" textAnchor="middle">GOING UP</text>
              </g>
            )}
            
            {direction === 'contracting' && (
              <g transform="translate(20, 10)">
                <polygon points="20,0 -40,30 20,60" fill="url(#quizRedGradient)" filter="url(#quizShadow)" />
                <rect x="0" y="10" width="220" height="40" rx="20" fill="url(#quizRedGradient)" filter="url(#quizShadow)" />
                <text x="110" y="38" fontSize="16" fontWeight="700" fill="white" textAnchor="middle">GOING DOWN</text>
              </g>
            )}
            
            {direction === 'steady' && (
              <g transform="translate(15, 10)">
                <rect x="0" y="10" width="250" height="40" rx="20" fill="url(#quizGrayGradient)" filter="url(#quizShadow)" />
                <text x="125" y="38" fontSize="18" fontWeight="700" fill="white" textAnchor="middle">
                  {currentLevel <= 3 ? 'STAGNANT' : 'STEADY'}
                </text>
              </g>
            )}
          </svg>
        </div>
      )}
      
      {/* Carousel Container */}
      <div 
        className="relative"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Navigation Arrows */}
        <button
          onClick={handlePrev}
          disabled={carouselIndex === 0}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full transition-all ${
            carouselIndex === 0 
              ? 'opacity-30 cursor-not-allowed' 
              : 'opacity-70 hover:opacity-100 hover:bg-white/10 cursor-pointer'
          }`}
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>
        
        <button
          onClick={handleNext}
          disabled={carouselIndex === 6}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full transition-all ${
            carouselIndex === 6 
              ? 'opacity-30 cursor-not-allowed' 
              : 'opacity-70 hover:opacity-100 hover:bg-white/10 cursor-pointer'
          }`}
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
        
        {/* Stage Card */}
        <div className="flex flex-col items-center px-16 py-4">
          {/* Stage Label */}
          <span className="text-xs text-gray-400 uppercase tracking-wide mb-3">
            {isCurrentStage ? 'Your Current Stage' : `Stage ${carouselIndex + 1} of 7`}
          </span>
          
          {/* Stage Box with full details */}
          <div 
            className={`w-72 rounded-xl p-5 shadow-2xl relative transition-all duration-300 ${
              isCurrentStage ? 'border-4 border-white' : 'border-2 border-white/30'
            }`}
            style={{ 
              backgroundColor: viewedStage.color,
              boxShadow: isCurrentStage ? `0 0 40px ${viewedStage.color}60` : `0 0 20px ${viewedStage.color}30`
            }}
          >
            {/* Stage Icon */}
            {(() => {
              const IconComponent = STAGE_ICONS[viewedStage.key];
              return IconComponent ? (
                <div className="flex justify-center mb-3">
                  <div className={`p-3 rounded-lg ${isCurrentStage ? 'bg-white/20' : 'bg-white/10'}`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                </div>
              ) : null;
            })()}
            
            <div className="text-center">
              <span className="text-white text-2xl font-bold block mb-1">{viewedStage.label}</span>
              <span className="text-white/80 text-sm font-medium block mb-3">{viewedStage.name}</span>
              <p className="text-white/90 text-sm leading-relaxed">{STAGE_DESCRIPTIONS[carouselIndex + 1]}</p>
            </div>
          </div>
          
          {/* YOU ARE HERE indicator (only for current stage) */}
          {isCurrentStage && (
            <div className="flex flex-col items-center mt-3">
              <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent" style={{ borderTopColor: viewedStage.color }}></div>
              <span className="text-xs text-gray-400 mt-1 font-medium">YOU ARE HERE</span>
            </div>
          )}
          
          {/* Navigation hint for non-current stages */}
          {!isCurrentStage && (
            <div className="mt-4">
              <button
                onClick={() => setCarouselIndex(currentLevel - 1)}
                className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
              >
                Return to your stage
              </button>
            </div>
          )}
        </div>
        
        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-2">
          {STAGES_DATA.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCarouselIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === carouselIndex 
                  ? 'bg-white w-4' 
                  : idx === currentLevel - 1 
                    ? 'bg-yellow-500' 
                    : 'bg-gray-600 hover:bg-gray-500'
              }`}
              title={idx === currentLevel - 1 ? 'Your current stage' : `Stage ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Direction-Based Analysis (only shown when viewing current stage) */}
      {isCurrentStage && currentDescription && (
        <div className="bg-gray-800/50 rounded-xl p-6 max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: directionColor }}
            ></div>
            Your Direction: {directionText}
          </h3>
          
          <div className="space-y-3 text-gray-300">
            <p className="font-medium">{currentDescription.title}</p>
            {currentDescription.body && (
              <p className="text-gray-400">{currentDescription.body}</p>
            )}
            {currentDescription.priority && (
              <p className="text-sm font-semibold mt-3 p-3 rounded-lg" style={{ 
                backgroundColor: `${directionColor}20`,
                color: directionColor 
              }}>
                {currentDescription.priority}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Retake Button */}
      <div className="text-center pt-4">
        <Button
          onClick={onRetake}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          Retake Assessment
        </Button>
      </div>
    </div>
  );
};

export default QuizResultCarousel;
